import React, { useState, useEffect } from 'react';
import { getBetConfigs, updateBetConfig, setCorrectAnswer, BetConfig, BetConfigFormData } from '../../api/betConfigsApi';
import Layout from '../../components/Layout';
import { PencilIcon } from '@heroicons/react/24/outline';
import BetConfigFormModal from '../../components/BetConfigFormModal';
import CorrectAnswerEditor from '../../components/CorrectAnswerEditor';
import SearchFilter from '../../components/SearchFilter';
import { sortData, filterData, SortState } from '../../utils/tableUtils';
import Table, { TableColumn } from '../../components/Table';
import { formatRound } from '../../utils/general';

const Points: React.FC = () => {
  const [configs, setConfigs] = useState<BetConfig[]>([]);
  const [displayedConfigs, setDisplayedConfigs] = useState<BetConfig[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [currentConfig, setCurrentConfig] = useState<BetConfig | null>(null);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [sortState, setSortState] = useState<SortState<BetConfig>>({
    key: 'id',
    direction: 'asc'
  });

  // Which config is currently having its correct answer edited
  const [editingAnswerId, setEditingAnswerId] = useState<number | null>(null);

  useEffect(() => {
    fetchConfigs();
  }, []);

  useEffect(() => {
    let filteredData = configs;
    if (searchTerm) {
      filteredData = filterData(configs, searchTerm, ['id', 'round', 'name', 'description', 'points']);
    }
    filteredData = sortData(filteredData, sortState.key, sortState.direction);
    setDisplayedConfigs(filteredData);
  }, [configs, searchTerm, sortState]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const fetchConfigs = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const fetchedConfigs = await getBetConfigs();
      setConfigs(fetchedConfigs);
    } catch (err) {
      console.error('Error fetching bet configurations:', err);
      setError('Failed to load bet configurations. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSort = (key: keyof BetConfig) => {
    setSortState(prevState => ({
      key,
      direction: prevState.key === key && prevState.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleEditConfig = (config: BetConfig): void => {
    setCurrentConfig(config);
    setIsModalOpen(true);
  };

  const handleSubmitConfig = async (configData: BetConfigFormData): Promise<void> => {
    if (!currentConfig) return;
    try {
      const updatedConfig = await updateBetConfig(currentConfig.id, configData);
      setConfigs(configs.map(c => c.id === updatedConfig.id ? updatedConfig : c));
      setSuccessMessage('Configuration updated successfully');
    } catch (err) {
      console.error('Error updating configuration:', err);
      throw err;
    }
  };

  const handleSaveAnswer = async (config: BetConfig, answer: Record<string, any>) => {
    try {
      const updated = await setCorrectAnswer(config.id, answer);
      setConfigs(configs.map(c => c.id === updated.id ? updated : c));
      setSuccessMessage(`Correct answer saved for "${config.name}" — user bets recalculated.`);
      setEditingAnswerId(null);
    } catch (err) {
      console.error('Error saving correct answer:', err);
      alert('Failed to save correct answer.');
    }
  };

  const columns: TableColumn<BetConfig>[] = [
    {
      key: 'id',
      title: 'ID',
      render: (config) => <span className="text-gray-400">{config.id}</span>,
      sortable: true
    },
    {
      key: 'round',
      title: 'Round',
      render: (config) => <span className="text-white">{formatRound(config.round)}</span>,
      sortable: true
    },
    {
      key: 'matchday',
      title: 'Matchday',
      render: (config) => <span className="text-gray-300">{config.matchday || 'N/A'}</span>,
      sortable: true
    },
    {
      key: 'name',
      title: 'Name',
      render: (config) => <span className="text-white">{config.name}</span>,
      sortable: true
    },
    {
      key: 'description',
      title: 'Description',
      render: (config) => <span className="text-gray-300">{config.description}</span>,
      sortable: true
    },
    {
      key: 'general',
      title: 'General',
      render: (config) => (
        <span className={config.general ? "text-stake-green" : "text-gray-400"}>
          {config.general ? "Yes" : "No"}
        </span>
      ),
      sortable: true
    },
    {
      key: 'points',
      title: 'Points',
      render: (config) => <span className="text-stake-blue font-medium">{config.points}</span>,
      sortable: true
    },
    {
      key: 'correct_answer' as any,
      title: 'Correct Answer',
      render: (config) => {
        if (!config.general) return <span className="text-gray-600">—</span>;

        if (editingAnswerId === config.id) {
          return (
            <CorrectAnswerEditor
              config={config}
              onSave={(answer) => handleSaveAnswer(config, answer)}
              onCancel={() => setEditingAnswerId(null)}
            />
          );
        }

        const current = config.correct_answer
          ? JSON.stringify(config.correct_answer)
          : null;

        return (
          <div className="flex items-center gap-2">
            <span className={`text-xs font-mono ${current ? 'text-yellow-400' : 'text-gray-600'}`}>
              {current ?? 'not set'}
            </span>
            <button
              onClick={() => setEditingAnswerId(config.id)}
              className="text-stake-blue hover:text-blue-300 p-1 rounded hover:bg-dark-200 transition-colors"
              title="Set correct answer"
            >
              <PencilIcon className="h-4 w-4" />
            </button>
          </div>
        );
      }
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (config) => (
        <button
          onClick={() => handleEditConfig(config)}
          className="text-stake-blue hover:text-blue-300 p-1 rounded hover:bg-dark-200 transition-colors"
        >
          <PencilIcon className="h-5 w-5" />
        </button>
      )
    }
  ];

  return (
    <Layout>
      <div className="-mx-3 sm:mx-0 sm:px-4 py-4">
        <div className="flex justify-between items-center mb-6 px-3 sm:px-0">
          <SearchFilter
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search configurations..."
            className="w-72"
          />
        </div>

        {error && (
          <div className="bg-stake-red bg-opacity-20 border border-stake-red text-white px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="bg-stake-green bg-opacity-20 border border-stake-green text-white px-4 py-3 rounded mb-4">
            {successMessage}
          </div>
        )}

        <Table<BetConfig>
          fullWidth
          compact
          data={displayedConfigs}
          columns={columns}
          keyExtractor={(config) => config.id}
          emptyMessage="No point configurations found"
          isLoading={isLoading}
          sortState={sortState}
          onSort={handleSort}
          searchTerm={searchTerm}
        />

        <BetConfigFormModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleSubmitConfig}
          config={currentConfig as BetConfig}
          title="Edit Configuration"
        />
      </div>
    </Layout>
  );
};

export default Points;
